import styled from "styled-components";

import {
  CASTE,
  COMPLEXION,
  EDUCATION,
  EMPLOYED_IN,
  FAMILY_TYPE,
  FAMILY_STATUS,
  GENDER,
  MOTHER_TOUNGE,
  PHYSICAL_STATUS,
  PROFILE_CREATED,
  RELIGION,
  STARS,
  ANNUAL_INCOME,
  MARITAL_STATUS
} from "../../helpers/dbFields";

import { COUNTRY, STATE } from "../../helpers/countries";
import { titleCase } from "../../helpers/common";

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  align-items: start;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Box = styled.section`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow);
  overflow: hidden;
  animation: details-in 0.6s var(--ease-out) both;

  &:nth-child(2) { animation-delay: 60ms; }
  &:nth-child(3) { animation-delay: 120ms; }
  &:nth-child(4) { animation-delay: 180ms; }
  &:nth-child(5) { animation-delay: 240ms; }
  &:nth-child(6) { animation-delay: 300ms; }

  @keyframes details-in {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: none; }
  }
`;

const ImageContainer = styled.div`
  padding: 16px;

  img {
    width: 100%;
    aspect-ratio: 4 / 5;
    object-fit: cover;
    border-radius: 14px;
  }
`;

const BoxHeader = styled.h3`
  margin: 0;
  padding: 16px 20px;
  font-size: 17px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-tint);
`;

const Rows = styled.dl`
  margin: 0;
  padding: 8px 20px 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(110px, 0.9fr) minmax(0, 1.4fr);
  gap: 12px;
  padding: 8px 0;
  font-size: 15px;

  dt {
    color: var(--text-muted);
    font-weight: 500;
  }

  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

const Line = ({ label, children }) => (
  <Row>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </Row>
);

const LineBreak = styled.div`
  height: 1px;
  margin: 8px 0;
  background: var(--border);
`;

const PremiumWrap = styled.div`
  padding: 0 20px 20px;
`;

const PremiumLink = styled.a`
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  color: var(--coral);
`;


const ProfileDetails = ({props}) => {
  const profile = props.profile;
  const profileContacts = props.profileContacts;
  
  const profileType = profile.premium ? 'Premium' : 'Normal';
  const profileCreated = profile.profile_created ? PROFILE_CREATED[profile.profile_created] : '';

  const amIPremium = props.me ? props.me.premium === true || props.me.premium === "true" ? true : false : true;

  const profilePhoto = profile.profile_pic ? profile.profile_pic : '';

  //Personal Information
  const displayName = profile.display_name ? profile.display_name : '';
  const fullName = profile.full_name ? profile.full_name : '';
  const gender = profile.gender ? GENDER[profile.gender]: '';
  const dob = profile.dob ? profile.dob.toDate().toDateString() : '';
  const complexion = profile.complexion ? COMPLEXION[profile.complexion] : '';
  const height = profile.height ? profile.height : '';
  const motherTounge = profile.mother_tounge ? MOTHER_TOUNGE[profile.mother_tounge] : '';
  const maritalStatus = profile.marital_status ? MARITAL_STATUS[profile.marital_status] : '';
  const physicalStatus = profile.physical_status ? PHYSICAL_STATUS[profile.physical_status] : '';
  const aboutMyself = profile.about_myself ? profile.about_myself : '';

  //Family and Religious Beliefs
  const familyType = profile.family_type ? FAMILY_TYPE[profile.family_type] : '';
  const familyStatus = profile.family_status ? FAMILY_STATUS[profile.family_status] : '';
  const aboutFamily = profile.about_family ? profile.about_family : '';
  const religion = profile.religion ? RELIGION[profile.religion] : '';
  const caste = profile.caste && profile.religion ? CASTE[profile.religion][profile.caste]: '';
  const star = profile.star ? STARS[profile.star] ? STARS[profile.star] : profile.star : '';
  const dosham = profile.dosham ? profile.dosham : '';
  
  //Education and Work
  const education = profile.education ? EDUCATION[profile.education] : '';
  const specialisation = profile.specialisation ? profile.specialisation : '';
  const employedIn = profile.employed ? EMPLOYED_IN[profile.employed] : '';
  const annualIncome = profile.annual_income ? ANNUAL_INCOME[profile.annual_income] ? ANNUAL_INCOME[profile.annual_income] : profile.annual_income : '';
  const countryOfWork = profile.country_of_work ? COUNTRY[profile.country_of_work] : '';
  const statesWorkList = STATE[profile.country_of_work];
  const stateOfWork = profile.state_of_work_name ? profile.state_of_work_name : profile.state_of_work ? statesWorkList[profile.state_of_work] : '';
  const cityOfWork = profile.city_of_work ? titleCase(profile.city_of_work.replace(/_/g , " ")) : "";
  const jobName = profile.job_name ? profile.job_name : '';
  const nameOfEmployer = profile.name_of_employer ? profile.name_of_employer : '';

  //Address and Contact details
  const address = profile.address ? profile.address : '';
  const mobile = amIPremium ? profileContacts ? profileContacts.mobile : '' : "Only premium member can view mobile number";
  const email = amIPremium ? profileContacts? profileContacts.email : '' : "Only premium member can view email";

  const country = profile.country ? COUNTRY[profile.country] : '';
  const statesList = STATE[profile.country];
  const state = profile.state_name ? profile.state_name : profile.state ? statesList[profile.state] : '';
  const city = profile.city ? titleCase(profile.city.replace(/_/g , " ")) : "";
  const zipCode = profile.zip_code ? profile.zip_code : '';  

  //Preferences
  const preferredAgeFrom = profile.preferred_age_from ? profile.preferred_age_from : '';
  const preferredAgeTo = profile.preferred_age_to ? profile.preferred_age_to : '';


  let preferredEducation = '';
  let preferredEmployment = '';
  if(Array.isArray(profile.preferred_education)) {
    preferredEducation = profile.preferred_education.map((emp)=> {
      if(emp == "ANY" || emp == "Any") return "Any";
      return EDUCATION[emp];
    }).join(", ");
  } else {
    preferredEducation = profile.preferred_education ? EDUCATION[profile.preferred_education] ? EDUCATION[profile.preferred_education] : profile.preferred_education : '';
  }

  if(Array.isArray(profile.preferred_employment)) {
    preferredEmployment = profile.preferred_employment.map((emp)=> {
      if(emp == "ANY" || emp == "Any") return "Any";
      return EMPLOYED_IN[emp];
    }).join(", ");
  } else {
    preferredEmployment = profile.preferred_employment ? EMPLOYED_IN[profile.preferred_employment] ? EMPLOYED_IN[profile.preferred_employment] : profile.preferred_employment : '';
  }
  
  const preferredHeightFrom = profile.preferred_height_from ? profile.preferred_height_from : '';
  const preferredHeightTo = profile.preferred_height_to ? profile.preferred_height_to : '';
  const starsPreferred = profile.stars_preferred ? profile.stars_preferred : '';
  const aboutPreferredPartner = profile.about_preferred_partner ? profile.about_preferred_partner : '';

  return (
    <Content>
      {profilePhoto && 
      <Box>
        <BoxHeader>Photo</BoxHeader>
        <ImageContainer>
          <img src={profilePhoto} alt={displayName || fullName}></img>
        </ImageContainer>
      </Box>
      }
      <Box>
        <BoxHeader>Personal Information</BoxHeader>
        <Rows>
        {displayName && <Line label="Display Name">{displayName}</Line>}
        <Line label="Full Name">{fullName}</Line>
        <Line label="Marital Status">{maritalStatus}</Line>
        <Line label="Gender">{gender}</Line>
        <Line label="Mother Tounge">{motherTounge}</Line>
        <Line label="Date of Birth">{dob}</Line>
        <Line label="Height">{height}</Line>
        <Line label="Complexion">{complexion}</Line>
        <Line label="Physical Status">{physicalStatus}</Line>
        <Line label="About my self">{aboutMyself}</Line>
        </Rows>
      </Box>

      <Box>
        <BoxHeader>Family and Religious Beliefs</BoxHeader>
        <Rows>
        <Line label="Family status">{familyStatus}</Line>
        <Line label="Family type">{familyType}</Line>
        <Line label="About family">{aboutFamily}</Line>
        <LineBreak></LineBreak>
        <Line label="Religion">{religion}</Line>
        <Line label="Caste">{caste}</Line>
        <Line label="Star">{star}</Line>
        <Line label="Dosham">{dosham}</Line>
        </Rows>
      </Box>

      <Box>
        <BoxHeader>Education and Work</BoxHeader>
        <Rows>
        <Line label="Education">{education}</Line>
        <Line label="Specialisation">{specialisation}</Line>
        <LineBreak></LineBreak>
        <Line label="Job">{jobName}</Line>
        <Line label="Employed">{employedIn}</Line>
        <Line label="Employer">{nameOfEmployer}</Line>
        <Line label="Annual Income">{annualIncome}</Line>
        <Line label="Country of work">{countryOfWork}</Line>
        <Line label="State of work">{stateOfWork}</Line>
        <Line label="City of work">{cityOfWork}</Line>
        </Rows>
      </Box>

      <Box>
        <BoxHeader>Address and Contact information</BoxHeader>
        <Rows>
        <Line label="Address">{address}</Line>
        <Line label="City">{city}</Line>
        <Line label="State">{state}</Line>
        <Line label="Country">{country}</Line>
        <Line label="Pincode">{zipCode}</Line>
        <Line label="Mobile Number">{mobile}</Line>
        <Line label="Email">{email}</Line>
        </Rows>
        {!amIPremium ? <PremiumWrap><PremiumLink href="/premium">Click here to upgrade to premium</PremiumLink></PremiumWrap> : null}
      </Box>
      
      <Box>
        <BoxHeader>Partner Preferences</BoxHeader>
        <Rows>
        <Line label="Preferred age">{preferredAgeFrom} to {preferredAgeTo}</Line>
        <Line label="Preferred height">{preferredHeightFrom} to {preferredHeightTo}</Line>
        <Line label="Preferred education">{preferredEducation}</Line>
        <Line label="Preferred employment">{preferredEmployment}</Line>
        <Line label="Star preferred">{starsPreferred}</Line>
        <Line label="About preferred partner">{aboutPreferredPartner}</Line>
        </Rows>
      </Box>
    </Content>
  )
};

export default ProfileDetails;
